import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Package, MapPin, Truck, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import Logo from "@/components/layout/Logo";
import Footer from "@/components/layout/Footer";
import { API } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const STAGES = [
  { key: "needs_approval", label: "Submitted", icon: Clock, color: "#9ca3af" },
  { key: "pending", label: "Approved", icon: Package, color: "#facc15" },
  { key: "onboard", label: "On the Road", icon: Truck, color: "#a78bfa" },
  { key: "done", label: "Delivered", icon: CheckCircle2, color: "#22c55e" },
];

const STATUS_LABEL = {
  needs_approval: { label: "Submitted — awaiting approval", color: "#9ca3af" },
  pending: { label: "Approved — awaiting pickup", color: "#facc15" },
  onboard: { label: "On the road", color: "#a78bfa" },
  done: { label: "Delivered", color: "#22c55e" },
  futile: { label: "Futile", color: "#ef4444" },
};

export default function PublicTrack() {
  const { jobRef } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState(jobRef || "");

  useEffect(() => {
    if (!jobRef) { setLoading(false); return; }
    setLoading(true);
    setErr(null);
    axios.get(`${API}/public/track/${encodeURIComponent(jobRef)}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Tracking number not found"))
      .finally(() => setLoading(false));
  }, [jobRef]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    nav(`/track/${encodeURIComponent(query.trim())}`);
  };

  const statusKey = data?.status;
  const meta = STATUS_LABEL[statusKey] || STATUS_LABEL.pending;
  const isFutile = statusKey === "futile";
  const stageIdx = STAGES.findIndex((s) => s.key === statusKey);

  return (
    <div className="min-h-screen satin-bg text-white">
      <header className="px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between max-w-[1500px] mx-auto">
        <Link to="/"><Logo size={48} /></Link>
        <nav className="hidden md:flex items-center gap-12">
          <Link to="/" className="nav-link">Home</Link>
          <Link to="/about" className="nav-link">About</Link>
          <Link to="/services" className="nav-link">Services</Link>
        </nav>
        {user ? (
          <Link to="/dashboard" className="pill-btn">DASHBOARD</Link>
        ) : (
          <Link to="/login" className="pill-btn">LOGIN</Link>
        )}
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-10 pb-16 fade-up">
        <p className="font-display tracking-[0.4em] text-[#19e6c8] text-xs">PUBLIC TRACKING</p>
        <h1 className="brand-title text-4xl md:text-5xl mt-3">Track your shipment</h1>
        <p className="text-white/70 mt-3">Enter your DRIVN FREIGHT carrier ref or client ref to see live status. No login required.</p>

        <form onSubmit={submitSearch} className="mt-8 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none" size={18} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter your DRIVN FREIGHT carrier ref or client ref"
              className="df-input has-icon-left"
              data-testid="track-input"
            />
          </div>
          <button type="submit" className="pill-btn !px-7" data-testid="track-submit">TRACK</button>
        </form>

        {loading && jobRef && <p className="text-white/50 mt-8">Looking up your shipment…</p>}

        {err && jobRef && (
          <div className="mt-8 rounded-2xl border border-red-500/30 bg-red-500/10 p-5 flex items-start gap-3" data-testid="track-error">
            <AlertTriangle className="text-red-400 mt-0.5" size={18} />
            <div>
              <p className="font-semibold">{err}</p>
              <p className="text-sm text-white/60 mt-1">Double-check your carrier ref (e.g. <code className="bg-white/10 rounded px-1">DRIVN-0508-003</code>) or client ref.</p>
            </div>
          </div>
        )}

        {data && (
          <div className="mt-10" data-testid="track-result">
            {/* Status card */}
            <div className="rounded-3xl p-5 sm:p-7 border border-white/10 backdrop-blur-sm" style={{ background: "rgba(255,255,255,0.025)" }}>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <p className="text-xs uppercase tracking-widest text-white/50">Booking Reference</p>
                  <p className="brand-title text-3xl text-[#19e6c8] mt-1">{data.job_ref}</p>
                  <p className="text-white/60 text-sm mt-1">Job date: {formatDate(data.job_date)}</p>
                </div>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold" style={{ background: meta.color + "22", color: meta.color }}>
                  <span className="dot" style={{ background: meta.color }} /> {meta.label}
                </span>
              </div>

              {/* Stage timeline */}
              {!isFutile && (
                <div className="mt-9">
                  <div className="grid grid-cols-4 gap-1 relative">
                    {STAGES.map((s, i) => {
                      const Icon = s.icon;
                      const reached = stageIdx >= i;
                      return (
                        <div key={s.key} className="flex flex-col items-center text-center">
                          <span
                            className="w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors"
                            style={{
                              background: reached ? s.color + "22" : "rgba(255,255,255,0.04)",
                              color: reached ? s.color : "rgba(255,255,255,0.4)",
                              border: reached ? `1px solid ${s.color}55` : "1px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <Icon size={15} />
                          </span>
                          <p className={`text-xs mt-2 ${reached ? "text-white" : "text-white/40"}`}>{s.label}</p>
                        </div>
                      );
                    })}
                    {/* Progress bar */}
                    <div className="absolute top-6 left-[12.5%] right-[12.5%] h-px bg-white/10 -z-0" />
                    <div
                      className="absolute top-6 left-[12.5%] h-[2px] -z-0 transition-all duration-700"
                      style={{
                        width: `${Math.max(0, (stageIdx / (STAGES.length - 1)) * 75)}%`,
                        background: "linear-gradient(90deg, #19e6c8, #6f4cff, #3b82f6)",
                      }}
                    />
                  </div>
                </div>
              )}

              {isFutile && (
                <div className="mt-6 rounded-xl bg-red-500/10 border border-red-500/30 p-4 text-sm text-red-200">
                  This delivery was marked futile. Please contact DRIVN FREIGHT operations for follow-up.
                </div>
              )}

              {/* Route */}
              <div className="mt-9 grid sm:grid-cols-2 gap-4">
                <RouteCard icon={MapPin} label="Pickup" text={data.pickup_area} tint="#19e6c8" />
                <RouteCard icon={MapPin} label="Drop-off" text={data.dropoff_area} tint="#3b82f6" />
              </div>

              <div className="mt-5 grid sm:grid-cols-3 gap-4 text-sm">
                <Pill k="Service" v={data.freight_type || "-"} />
                <Pill k="Items" v={`${data.item_qty || 0} × ${data.item_type || "-"}`} />
                <Pill k="POD" v={data.has_pod ? "Captured" : (data.status === "done" ? "Pending" : "Awaiting delivery")} />
              </div>

              {data.pod_completed_at && (
                <p className="text-xs text-white/50 mt-6">
                  Delivered at: {formatDateTime(data.pod_completed_at)}
                </p>
              )}
            </div>

            <p className="text-white/40 text-xs mt-5 text-center">
              For privacy reasons, exact pickup &amp; drop-off street numbers are not shown publicly.
            </p>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function RouteCard({ icon: Icon, label, text, tint }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10" style={{ background: "rgba(255,255,255,0.02)" }}>
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: tint + "22", color: tint }}>
          <Icon size={14} />
        </span>
        <span className="text-xs uppercase tracking-widest text-white/50">{label}</span>
      </div>
      <p className="text-white mt-3 text-base">{text || "—"}</p>
    </div>
  );
}

function Pill({ k, v }) {
  return (
    <div className="rounded-xl border border-white/10 px-4 py-3" style={{ background: "rgba(255,255,255,0.02)" }}>
      <p className="text-[10px] uppercase tracking-widest text-white/50">{k}</p>
      <p className="text-white text-sm mt-1 font-semibold">{v}</p>
    </div>
  );
}
